import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Page, Text, Card, Banner, BlockStack, Select, Box, Button } from '@shopify/polaris';
import { useState } from 'react';
import usePosApi from '../hooks/Api';
import PlanetEmbed from '../components/PlanetEmbed';
import * as Sentry from '@sentry/react';

function FormOrderPage() {
    const navigate = useNavigate();
    const { planetApi } = usePosApi();

    const [searchParams] = useSearchParams();
    const orderIds = searchParams.get('ids')?.split(',') ?? [];
    const source = searchParams.get('source') ?? 'list';
    const [preissuingErrors, setPreissuingErrors] = useState(null);
    const [formId, setFormId] = useState(null);
    const [fiscalNumber, setFiscalNumber] = useState(null);
    const [availableFiscalNumbers, setAvailableFiscalNumbers] = useState(null);
    const [availableInvoiceNumbers, setAvailableInvoiceNumbers] = useState(null);
    const [selectedFiscalNumber, setSelectedFiscalNumber] = useState(null);
    const [token, setToken] = useState(null);
    const [isSandbox, setIsSandbox] = useState(true);
    const [showFormSubmit, setShowFormSubmit] = useState(false);
    const [environment, setEnvironment] = useState(null);

    const [orders, setOrders] = useState([]);

    async function getPreIssuedFormRequest(orders, source, fiscalNumber = '', invoiceNumbers = '') {
        const response = await planetApi.getOrCreatePreIssuedForm(orders, source, fiscalNumber, invoiceNumbers);

        if (response.ok) {
            return response.data;
        }

        console.log(response);

        if (response.response.status == 422 && (response.data?.fiscal_numbers || response.data?.invoice_numbers)) {
            if (response.data?.fiscal_numbers && response.data.fiscal_numbers.length > 0) {
                setAvailableFiscalNumbers(response.data.fiscal_numbers);
            }
            if (response.data?.invoice_numbers && response.data.invoice_numbers.length > 0) {
                setAvailableInvoiceNumbers(response.data.invoice_numbers);
            }
            throw new Error('Validation errors');
        } else {
            setPreissuingErrors(response.data);
            throw new Error('Error preissuing the form - ' + JSON.stringify(response.data));
        }
    }

    function getPreIssuedForm(orderIds, source, fiscalNumber = '', invoiceNumbers = '') {
        shopify.loading(true);
        setAvailableFiscalNumbers(null);
        setAvailableInvoiceNumbers(null);
        setPreissuingErrors(null);
        getPreIssuedFormRequest(orderIds, source, fiscalNumber, invoiceNumbers) // I'm sending a single item in the array, the idea is to send multiple orders in the future
            .then(({ planet_form_id, fiscal_number, token, is_sandbox, orders, environment }) => {
                setFormId(planet_form_id);
                setFiscalNumber(fiscal_number);
                setToken(token);
                setIsSandbox(is_sandbox);
                setEnvironment(environment);
                setOrders(orders);
            })
            .finally(() => {
                shopify.loading(false);
                setSelectedFiscalNumber(null);
            });
    }

    function updateSelectedInvoiceNumbers(orderName, selectedInvoiceNumber) {
        const updatedAvailableInvoiceNumbers = availableInvoiceNumbers.map((orderInvoiceNumbers) => {
            if (orderInvoiceNumbers.orderName == orderName) {
                return {
                    orderName: orderInvoiceNumbers.orderName,
                    invoiceNumbers: orderInvoiceNumbers.invoiceNumbers,
                    selectedInvoiceNumber: selectedInvoiceNumber,
                };
            } else {
                return orderInvoiceNumbers;
            }
        });
        setAvailableInvoiceNumbers(updatedAvailableInvoiceNumbers);
    }

    useEffect(() => {
        getPreIssuedForm(orderIds, source);
    }, []);

    useEffect(() => {
        const allInvoicesSelected = areAllInvoicesSelected();
        setShowFormSubmit(
            (availableFiscalNumbers == null || selectedFiscalNumber) &&
                (availableInvoiceNumbers == null || allInvoicesSelected) &&
                (availableFiscalNumbers || availableInvoiceNumbers),
        );
    }, [selectedFiscalNumber, availableInvoiceNumbers]);

    function areAllInvoicesSelected() {
        if (availableInvoiceNumbers == null) return false;
        for (const invoiceNumber of availableInvoiceNumbers) {
            if (!invoiceNumber.selectedInvoiceNumber) {
                return false;
            }
        }
        return true;
    }

    async function onSuccess(data) {
        await planetApi.completePreIssuedForm({
            preIssuedFormId: data.preIssuedFormNumber,
            barcode: data.barcode,
            refundMethod: data.refundMethod,
            refundAmount: data.refundAmount,
            pdf: data.pdf,
        });

        navigate(`/pre-issued-forms/${formId}/details`);
    }

    async function onError(data) {
        console.log('PLANET.ERROR', data);
        Sentry.captureException(data);
    }

    async function onClose(data) {
        navigate(-1);
    }

    return (
        <Page narrowWidth title="Issuing Tax Free Form" backAction={{ content: 'Index', onAction: () => navigate(-1) }}>
            {preissuingErrors && (
                <Banner
                    tone="critical"
                    title="Some errors occured"
                    onDismiss={() => {
                        setPreissuingErrors(null);
                    }}
                >
                    {preissuingErrors?.statusCode && (
                        <Text tone="critical">statusCode: {preissuingErrors?.statusCode ?? '(Unknown)'}</Text>
                    )}
                    {preissuingErrors?.message && <Text tone="critical">{preissuingErrors.message}</Text>}
                    {preissuingErrors?.errors &&
                        Object.keys(preissuingErrors.errors).map((key) => (
                            <Text tone="critical">
                                {key}: {preissuingErrors.errors[key]}
                            </Text>
                        ))}
                </Banner>
            )}

            {availableFiscalNumbers && (
                <Box padding={'300'}>
                    <BlockStack gap={'300'}>
                        <Text variant="headingMd">Multiple fiscal numbers were found. Please select one</Text>
                        <Select
                            label="Available Fiscal Numbers"
                            value={selectedFiscalNumber}
                            onChange={(value) => setSelectedFiscalNumber(value)}
                            options={[
                                { label: 'Select', value: '' },
                                ...availableFiscalNumbers.map((availableFiscalNumber) => ({
                                    label: availableFiscalNumber,
                                    value: availableFiscalNumber,
                                })),
                            ]}
                        />
                    </BlockStack>
                </Box>
            )}

            {availableInvoiceNumbers && (
                <Box padding={'300'}>
                    <BlockStack gap={'300'}>
                        <Text variant="headingMd">Multiple invoice numbers were found. Please select one</Text>
                        {availableInvoiceNumbers.map((orderInvoiceNumbers) => (
                            <Select
                                label={`Available Invoice Numbers for Order ${orderInvoiceNumbers.orderName}`}
                                value={orderInvoiceNumbers.selectedInvoiceNumber ?? ''}
                                onChange={(value) => updateSelectedInvoiceNumbers(orderInvoiceNumbers.orderName, value)}
                                options={[
                                    { label: 'Select', value: '' },
                                    ...orderInvoiceNumbers.invoiceNumbers.map((invoiceNumber) => ({
                                        label: invoiceNumber,
                                        value: invoiceNumber,
                                    })),
                                ]}
                            />
                        ))}
                    </BlockStack>
                </Box>
            )}

            {showFormSubmit && (
                <Box padding={'300'}>
                    <Button
                        variant="secondary"
                        onClick={() =>
                            getPreIssuedForm(orderIds, source, selectedFiscalNumber, availableInvoiceNumbers)
                        }
                    >
                        Complete Form
                    </Button>
                </Box>
            )}

            {!preissuingErrors && !availableFiscalNumbers && !availableInvoiceNumbers && (
                <Card title="We are processing your request">
                    <BlockStack gap={400}>
                        <Text>Please hold on while we process your request and pre issue the Tax Free Form.</Text>

                        {formId && fiscalNumber !== null && token && (
                            <BlockStack gap={300}>
                                {fiscalNumber != '' && <Text fontWeight="bold">Fiscal Number: {fiscalNumber}</Text>}
                                {orders.length > 0 && <Text fontWeight="bold">Orders: {orders.join(', ')}</Text>}
                                <Text>Use the button below to open the Planet Free Tax Form.</Text>
                                <PlanetEmbed
                                    accessToken={token}
                                    formGuid={formId}
                                    fiscalNumber={fiscalNumber}
                                    isSandbox={isSandbox}
                                    onSuccess={onSuccess}
                                    onError={onError}
                                    onClose={onClose}
                                    environment={environment}
                                />
                            </BlockStack>
                        )}
                    </BlockStack>
                </Card>
            )}
        </Page>
    );
}

export default FormOrderPage;
